/**
 * PATH: packages/email-hub/src/feature/hooks/useEmailLabelPrefs.ts
 * FILE_ID: EMAILHUB-FEATURE-HOOK-LABELPREFS-001
 * PURPOSE: Read the viewer's PRIVATE personal email-label categories AND
 *          the org-shared CUSTOM categories (both tiers of the
 *          label-preferences payload) so the inbox and reader can OFFER
 *          them in the category picker and RENDER them when applied.
 * WHY:     Operator 2026-06-16: "where can I label it personal". Personal
 *          categories already existed (Settings → Email → Label preferences
 *          creates them), but there was no surface to APPLY one to an email.
 * FLOW:
 *   ┌──────────────────────────────┐   ┌───────────────┐   ┌──────────────┐
 *   │ GET /api/email/               │ → │ personal tier │ → │ module cache │
 *   │ label-preferences/           │   │ (5-min TTL)   │   │ + pub/sub    │
 *   └──────────────────────────────┘   └───────────────┘   └──────────────┘
 *   Every CategoryChip / context menu shares the ONE fetch (manual cache —
 *   email-hub hooks don't use React Query).
 * RELATED:
 *   - packages/ui/src/components/settings/email-label-prefs/* — the CRUD UI
 *     that creates these categories.
 *   - lib/categoryConfig.ts: getCategoryConfig(key, personalCategories)
 *     resolves an applied `personal.<key>` to its label + colour.
 */
import { useEffect, useState } from 'react'
import { emailApi } from '../lib/api'

export interface PersonalCategoryOption {
  /** Namespaced slug stored on the email's `category` (e.g. `personal.family`). */
  slug: string
  /** Display label ("Family", "Home bills"). */
  label: string
  /** Hex colour the user picked (`#RRGGBB`); '' when unset. */
  color: string
  /** DB id — needed to resolve `parentId` into a parent option. */
  id?: number
  /** Folders-style nesting (2026-07-06): the parent label's DB id in the
   *  same tier, or null/undefined for a top-level label. */
  parentId?: number | null
}

/** An org-shared CUSTOM label (`org.<key>`) — same render shape as the
 *  personal tier so pickers and `getCategoryConfig` treat both uniformly. */
export type OrgCategoryOption = PersonalCategoryOption

/** Shape returned by `POST /api/email/personal-categories/` and
 *  `POST /api/email/org-categories/`. */
interface CreatedPersonalCategory {
  slug?: string
  key?: string
  label?: string
  color?: string
}

interface LabelPrefsResponse {
  groups?: Array<{
    id?: string
    categories?: Array<{
      id?: number
      slug?: string
      key?: string
      label?: string
      color?: string
      is_active?: boolean
      parent_id?: number | null
    }>
  }>
}

interface LabelTiers {
  personal: PersonalCategoryOption[]
  orgCustom: OrgCategoryOption[]
}

const TTL_MS = 5 * 60 * 1000
let cache: { ts: number; data: LabelTiers } | null = null
let inflight: Promise<LabelTiers> | null = null
const subscribers = new Set<(data: LabelTiers) => void>()

/** Pure: response → render-ready personal category options. */
export function normalisePersonalCategories(
  res: LabelPrefsResponse | null,
): PersonalCategoryOption[] {
  const personal = res?.groups?.find((g) => g.id === 'personal')
  const cats = personal?.categories ?? []
  return cats
    .filter((c) => c.is_active !== false && (c.slug || c.key) && c.label)
    .map((c) => ({
      slug: c.slug || `personal.${c.key}`,
      label: c.label as string,
      color: c.color || '',
      id: c.id,
      parentId: c.parent_id ?? null,
    }))
}

/** Pure: response → render-ready org CUSTOM options (the `org.<key>` slice
 *  of the organisation group; built-ins have bare-key slugs and are
 *  filtered out — the static picker config already covers them). */
export function normaliseOrgCustomCategories(
  res: LabelPrefsResponse | null,
): OrgCategoryOption[] {
  const org = res?.groups?.find((g) => g.id === 'organisation')
  const cats = org?.categories ?? []
  return cats
    .filter(
      (c) =>
        c.is_active !== false &&
        c.label &&
        (c.slug || '').startsWith('org.'),
    )
    .map((c) => ({
      slug: c.slug as string,
      label: c.label as string,
      color: c.color || '',
      id: c.id,
      parentId: c.parent_id ?? null,
    }))
}

/** A label option annotated with its nesting depth for rendering. */
export interface NestedCategoryOption extends PersonalCategoryOption {
  /** 0 = top level, 1 = nested under the preceding depth-0 option. */
  depth: number
}

/**
 * Order one tier's options folders-style: each top-level label followed
 * immediately by its children (one level deep, matching the backend
 * contract). Orphans whose parent id doesn't resolve render at top
 * level — never dropped. Pure I→O.
 */
export function nestLabelOptions(
  options: PersonalCategoryOption[],
): NestedCategoryOption[] {
  const byParent = new Map<number, PersonalCategoryOption[]>()
  const tops: PersonalCategoryOption[] = []
  const ids = new Set(options.map((o) => o.id).filter((v) => v != null))
  for (const opt of options) {
    if (opt.parentId != null && ids.has(opt.parentId)) {
      const kids = byParent.get(opt.parentId) ?? []
      kids.push(opt)
      byParent.set(opt.parentId, kids)
    } else {
      tops.push(opt)
    }
  }
  const out: NestedCategoryOption[] = []
  for (const top of tops) {
    out.push({ ...top, depth: 0 })
    if (top.id != null) {
      for (const kid of byParent.get(top.id) ?? []) {
        out.push({ ...kid, depth: 1 })
      }
    }
  }
  return out
}

async function load(): Promise<LabelTiers> {
  if (inflight) return inflight
  inflight = (async () => {
    try {
      const res = await emailApi<LabelPrefsResponse>(
        '/api/email/label-preferences/',
        // Cosmetic, fail-soft personal-label read — it must NEVER arm the
        // tab-wide identity-mismatch RELOAD on a cookie/bearer drift session
        // (cookie=user 18, bearer=user 19). Recurrence of
        // BUG-2026-06-10-VIEWPREF-IDENTITY-RELOAD-LOOP / -06-18-GMAIL-STATUS,
        // same class as the gmail/outlook status polls. Genuine drift is still
        // caught by every DATA-bearing call + the IdentityMismatchBanner.
        { skipIdentityAssert: true },
      )
      const data: LabelTiers = {
        personal: normalisePersonalCategories(res),
        orgCustom: normaliseOrgCustomCategories(res),
      }
      cache = { ts: Date.now(), data }
      subscribers.forEach((fn) => fn(data))
      return data
    } catch {
      // Graceful: no label tiers (endpoint missing / not authed / offline)
      // → behave as "no custom categories" rather than crashing the picker.
      return cache?.data ?? { personal: [], orgCustom: [] }
    } finally {
      inflight = null
    }
  })()
  return inflight
}

/** Shared subscription plumbing for both tier hooks. */
function useLabelTier<T>(select: (tiers: LabelTiers) => T, empty: T): T {
  const [data, setData] = useState<T>(cache ? select(cache.data) : empty)
  useEffect(() => {
    let alive = true
    const sub = (d: LabelTiers) => {
      if (alive) setData(select(d))
    }
    subscribers.add(sub)
    const fresh = cache && Date.now() - cache.ts < TTL_MS
    if (fresh) setData(select(cache!.data))
    else void load()
    return () => {
      alive = false
      subscribers.delete(sub)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- select is a
    // stable module-level projection at every call site.
  }, [])
  return data
}

const selectPersonal = (t: LabelTiers) => t.personal
const selectOrgCustom = (t: LabelTiers) => t.orgCustom

/**
 * The viewer's personal label categories (cached, shared). Safe to call
 * from many components at once — they share a single network fetch and all
 * re-render when it resolves. Returns `[]` until loaded or on any error.
 */
export function usePersonalCategories(): PersonalCategoryOption[] {
  return useLabelTier(selectPersonal, [])
}

/**
 * The organisation's shared CUSTOM label categories (`org.<key>`), from the
 * same cached label-preferences fetch as the personal tier.
 */
export function useOrgCustomCategories(): OrgCategoryOption[] {
  return useLabelTier(selectOrgCustom, [])
}

/**
 * Create a new PRIVATE personal label and return it ready to apply to an
 * email. Reuses the same `POST /api/email/personal-categories/` endpoint the
 * Settings → Email → Label preferences UI uses, so a category can be born
 * straight from the Categorise picker (operator 2026-06-21: "I can't create
 * new categories in Personal … or otherwise"). The created row is pushed into
 * the shared cache + broadcast so EVERY open picker repaints with it
 * immediately, then a background reload reconciles against the server truth.
 *
 * @throws if the request fails (caller surfaces the toast).
 */
export async function createPersonalCategory(
  label: string,
  opts: { color?: string } = {},
): Promise<PersonalCategoryOption> {
  const trimmed = (label || '').trim()
  if (!trimmed) throw new Error('Label is required')

  const body: Record<string, unknown> = { label: trimmed.slice(0, 80) }
  if (opts.color) body.color = opts.color

  const res = await emailApi<CreatedPersonalCategory>(
    '/api/email/personal-categories/',
    { method: 'POST', body: JSON.stringify(body) },
  )

  const created: PersonalCategoryOption = {
    // The server namespaces the slug (`personal.<key>`); fall back defensively
    // so a partial response can never yield an unusable bare slug.
    slug: res.slug || (res.key ? `personal.${res.key}` : ''),
    label: res.label || trimmed,
    color: res.color || opts.color || '',
  }
  if (!created.slug) throw new Error('Server did not return a category slug')

  broadcastCreated('personal', created)
  return created
}

/**
 * Create a new ORG-SHARED custom label (`POST /api/email/org-categories/`).
 * Phase 2 P20: non-staff creates return a proposal (202) — the label is
 * NOT applied until an approver accepts. Staff may pass autoAccept.
 */
export type OrgCategoryCreateResult =
  | { kind: 'created'; category: OrgCategoryOption }
  | {
      kind: 'proposed'
      proposalId: string
      message: string
    }

export async function createOrgCategory(
  label: string,
  opts: { color?: string; autoAccept?: boolean } = {},
): Promise<OrgCategoryCreateResult> {
  const trimmed = (label || '').trim()
  if (!trimmed) throw new Error('Label is required')

  const body: Record<string, unknown> = { label: trimmed.slice(0, 80) }
  if (opts.color) body.color = opts.color
  if (opts.autoAccept) body.auto_accept = true

  const res = await emailApi<
    CreatedPersonalCategory & {
      status?: string
      proposal?: { proposal_id?: string; proposalId?: string }
      message?: string
    }
  >('/api/email/org-categories/', {
    method: 'POST',
    body: JSON.stringify(body),
  })

  // 202 propose-only path
  if (res?.status === 'proposed' || res?.proposal) {
    const proposalId = String(
      res.proposal?.proposal_id || res.proposal?.proposalId || '',
    )
    return {
      kind: 'proposed',
      proposalId,
      message:
        res.message ||
        'Org-shared label proposed for approval — it will appear after accept.',
    }
  }

  const created: OrgCategoryOption = {
    slug: res.slug || (res.key ? `org.${res.key}` : ''),
    label: res.label || trimmed,
    color: res.color || opts.color || '',
  }
  if (!created.slug) throw new Error('Server did not return a category slug')

  broadcastCreated('orgCustom', created)
  return { kind: 'created', category: created }
}

/** Optimistic cache insert + broadcast (de-duped on slug so a reload can't
 *  double it), then a background reload reconciles with the server truth. */
function broadcastCreated(
  tier: keyof LabelTiers,
  created: PersonalCategoryOption,
): void {
  const current: LabelTiers = cache?.data ?? { personal: [], orgCustom: [] }
  const next: LabelTiers = {
    ...current,
    [tier]: [
      ...current[tier].filter((c) => c.slug !== created.slug),
      created,
    ],
  }
  cache = { ts: Date.now(), data: next }
  subscribers.forEach((fn) => fn(next))
  void load()
}

/** Test-only: reset the module cache so each test starts clean. */
export function __resetPersonalCategoriesCache(): void {
  cache = null
  inflight = null
  subscribers.clear()
}
